import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ShieldStar, MagnifyingGlass, DotsThreeVertical, UserSwitch, Trash, ClockCounterClockwise } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import ConfirmDialog from '@/components/ConfirmDialog';
import { toast } from 'sonner';

export default function AdminUsers() {
    const qc = useQueryClient();
    const nav = useNavigate();
    const [q, setQ] = useState('');
    const [del, setDel] = useState(null);
    const { data: users = [], isLoading } = useQuery({
        queryKey: ['admin-users'],
        queryFn: async () => (await api.get('/admin/users')).data,
    });

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return users;
        return users.filter(u => (u.name || '').toLowerCase().includes(s) || (u.email || '').toLowerCase().includes(s));
    }, [users, q]);

    const impersonate = async (u) => {
        try {
            await api.post(`/admin/impersonate/${u.user_id}`);
            toast.success(`Now viewing as ${u.name || u.email}`);
            nav('/dashboard');
            // Reload to refresh AuthContext with the new session
            window.location.href = '/dashboard';
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to impersonate');
        }
    };

    const doDelete = async () => {
        try {
            const { data } = await api.delete(`/admin/users/${del.user_id}`);
            toast.success(`Deleted user — ${data.facilities_removed} household(s), ${data.patients_removed} member(s)`);
            setDel(null);
            qc.invalidateQueries({ queryKey: ['admin-users'] });
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
        }
    };

    return (
        <div className="px-6 md:px-10 py-8 max-w-7xl" data-testid="admin-users-root">
            <div className="flex items-end justify-between mb-6">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 flex items-center gap-1.5">
                        <ShieldStar size={13} weight="duotone" /> Tanow staff
                    </div>
                    <h1 className="font-heading text-4xl md:text-5xl text-slate-900 mt-1">All users</h1>
                    <p className="text-slate-600 mt-2 max-w-2xl">Every Tanow coordinator — impersonate to troubleshoot, or wipe a stuck account.</p>
                </div>
                <Link to="/admin/audit" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
                    <ClockCounterClockwise size={14} /> Audit log
                </Link>
            </div>

            <div className="mb-4 relative max-w-md">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input data-testid="admin-users-search" placeholder="Search by name or email…" value={q} onChange={e => setQ(e.target.value)} className="pl-9" />
            </div>

            <div className="rounded-xl border border-slate-200 bg-card shadow-sm overflow-hidden">
                <div className="grid grid-cols-[1fr_1fr_repeat(5,80px)_60px] px-5 py-3 border-b border-slate-200 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                    <div>User</div><div>Email</div><div>Homes</div><div>People</div><div>Notes</div><div>Labs</div><div>Sent</div><div />
                </div>
                {isLoading ? (
                    <div className="px-5 py-8 text-sm text-slate-500 text-center">Loading…</div>
                ) : filtered.length === 0 ? (
                    <div className="px-5 py-8 text-sm text-slate-500 text-center">No users found.</div>
                ) : (
                    <div className="divide-y divide-slate-100">
                        {filtered.map(u => (
                            <div key={u.user_id} data-testid="admin-user-row" className="grid grid-cols-[1fr_1fr_repeat(5,80px)_60px] px-5 py-3 items-center text-sm">
                                <div className="min-w-0">
                                    <Link to={`/admin/users/${u.user_id}`} className="font-medium text-slate-900 hover:underline truncate flex items-center gap-2">
                                        {u.picture ? <img src={u.picture} alt="" className="w-6 h-6 rounded-full" /> : null}
                                        <span className="truncate">{u.name || '(no name)'}</span>
                                        {u.is_admin && <span className="text-[9px] uppercase tracking-widest bg-primary/10 text-primary px-1.5 py-0.5 rounded">Admin</span>}
                                    </Link>
                                    <div className="text-[11px] text-slate-500 truncate">Joined {(u.created_at || '').slice(0, 10)}</div>
                                </div>
                                <div className="text-slate-700 truncate">{u.email}</div>
                                <div className="text-slate-800">{u.stats?.households ?? 0}</div>
                                <div className="text-slate-800">{u.stats?.members ?? 0}</div>
                                <div className="text-slate-800">{u.stats?.notes ?? 0}</div>
                                <div className="text-slate-800">{u.stats?.labs ?? 0}</div>
                                <div className="text-slate-800">{u.stats?.check_ins ?? 0}</div>
                                <div className="text-right">
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <button className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid="admin-user-actions" aria-label="Actions">
                                                <DotsThreeVertical size={18} weight="bold" />
                                            </button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            <DropdownMenuItem asChild>
                                                <Link to={`/admin/users/${u.user_id}`}>
                                                    <ArrowRight size={14} className="mr-2" /> View details
                                                </Link>
                                            </DropdownMenuItem>
                                            <DropdownMenuItem data-testid="admin-impersonate" onClick={() => impersonate(u)} disabled={u.is_admin}>
                                                <UserSwitch size={14} className="mr-2" /> Impersonate
                                            </DropdownMenuItem>
                                            <DropdownMenuItem data-testid="admin-delete-user" onClick={() => setDel(u)} disabled={u.is_admin} className="text-red-600 focus:text-red-700">
                                                <Trash size={14} className="mr-2" /> Delete user & data
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <ConfirmDialog
                open={!!del} onOpenChange={(v) => !v && setDel(null)}
                title={`Delete ${del?.name || del?.email}?`}
                description={del ? `Removes this user and all households they solely own, plus every member, medication, lab, appointment, note, and sent check-in. Households they share with another coordinator will simply remove them. This can't be undone.` : ''}
                confirmLabel="Yes, delete user + data"
                onConfirm={doDelete}
            />
        </div>
    );
}
